import { Reducer } from 'redux';
import { INITIAL_PO_REVIEW_DRAFT_STATE } from '../../constants';
import {
    PO_REVIEW_DRAFT_CLEAR,
    PO_REVIEW_DRAFT_DELIVERY_DATE_CHANGED,
    PO_REVIEW_DRAFT_GET,
    PO_REVIEW_DRAFT_LINE_ADDED,
    PO_REVIEW_DRAFT_LINE_REMOVED,
    PO_REVIEW_DRAFT_LOADED,
    PO_REVIEW_DRAFT_PO_NUMBER_CHANGED,
    PO_REVIEW_DRAFT_ROW_ITEM_CHANGED,
    PO_REVIEW_DRAFT_ROW_QUANTITY_CHANGED,
    PO_REVIEW_DRAFT_SHIP_DATE_CHANGED,
    PO_REVIEW_DRAFT_SHIPPING_LOCATION_CODE_CHANGED,
    PO_REVIEW_DRAFT_STATUS_CHANGED,
    PoReviewDraftActions,
} from '../actions/poReviewDraftActions';
import { PoReviewDraft, PoReviewDraftState, PoReviewRow } from '../types';

const updateDraft = (
    state: PoReviewDraftState,
    update: (draft: PoReviewDraft) => PoReviewDraft
): PoReviewDraftState => ({
    ...state,
    draft: {
        ...state.draft,
        value: state.draft.value ? update(state.draft.value) : undefined,
    },
});

const updateRow = (draft: PoReviewDraft, rowId: string, changes: Partial<PoReviewRow>): PoReviewDraft => ({
    ...draft,
    rows: draft.rows.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)),
});

// A line number past every current one, so an added line sorts after the extracted lines and removing
// a line never renumbers the rest.
const nextItemNumber = (rows: PoReviewRow[]): number =>
    rows.reduce((highest, row) => Math.max(highest, row.itemNumber), 0) + 1;

// An added line starts empty and unresolved, so it blocks submit until the reviewer fills it in.
const addedRow = (rows: PoReviewRow[], rowId: string): PoReviewRow => ({
    rowId,
    itemNumber: nextItemNumber(rows),
    isAdded: true,
    quantity: '',
    findings: [],
});

export const poReviewDraftReducer: Reducer<PoReviewDraftState, PoReviewDraftActions> = (
    state = INITIAL_PO_REVIEW_DRAFT_STATE,
    action
): PoReviewDraftState => {
    switch (action.type) {
        case PO_REVIEW_DRAFT_GET:
        case PO_REVIEW_DRAFT_LOADED:
            return {
                ...state,
                draft: {
                    value: action.payload.value,
                    error: action.payload.error,
                    status: action.payload.status,
                },
            };
        case PO_REVIEW_DRAFT_CLEAR:
            return INITIAL_PO_REVIEW_DRAFT_STATE;
        case PO_REVIEW_DRAFT_PO_NUMBER_CHANGED:
            return updateDraft(state, (draft) => ({ ...draft, poNumber: action.payload }));
        case PO_REVIEW_DRAFT_SHIP_DATE_CHANGED:
            return updateDraft(state, (draft) => ({ ...draft, shipDate: action.payload }));
        case PO_REVIEW_DRAFT_DELIVERY_DATE_CHANGED:
            return updateDraft(state, (draft) => ({ ...draft, deliveryDate: action.payload }));
        case PO_REVIEW_DRAFT_SHIPPING_LOCATION_CODE_CHANGED:
            return updateDraft(state, (draft) => ({ ...draft, shippingLocationCode: action.payload }));
        case PO_REVIEW_DRAFT_STATUS_CHANGED:
            return updateDraft(state, (draft) => ({ ...draft, status: action.payload }));
        case PO_REVIEW_DRAFT_ROW_ITEM_CHANGED:
            return updateDraft(state, (draft) =>
                updateRow(draft, action.payload.rowId, {
                    itemId: action.payload.itemId,
                })
            );
        case PO_REVIEW_DRAFT_ROW_QUANTITY_CHANGED:
            return updateDraft(state, (draft) =>
                updateRow(draft, action.payload.rowId, { quantity: action.payload.quantity })
            );
        case PO_REVIEW_DRAFT_LINE_ADDED:
            return updateDraft(state, (draft) => ({
                ...draft,
                rows: [...draft.rows, addedRow(draft.rows, action.payload.rowId)],
            }));
        case PO_REVIEW_DRAFT_LINE_REMOVED:
            return updateDraft(state, (draft) => ({
                ...draft,
                rows: draft.rows.filter((row) => row.rowId !== action.payload.rowId),
            }));
        default:
            return state;
    }
};
